import React, { useState } from "react";
import { ArrowUpRight, Plus, Minus } from "lucide-react";
import Nav from "../components/sections/Nav";
import Footer from "../components/sections/Footer";
import Seo from "../components/Seo";
import Marquee from "../components/sections/Marquee";
import SectionDivider from "../components/SectionDivider";
import RevealOnScroll from "../components/RevealOnScroll";

const BOOKING_URL = "https://alchemy-practice.clientsecure.me/";

const Eyebrow = ({ children, className = "" }) => (
    <span className={`editorial-eyebrow ${className}`}>{children}</span>
);

const CTA = ({ children = "Schedule a Consultation", testid }) => (
    <a
        href={BOOKING_URL}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={testid}
        className="editorial-button-primary group"
    >
        {children}
        <ArrowUpRight size={16} strokeWidth={1.25} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
    </a>
);

const SageRule = () => (
    <div className="mx-auto" style={{ height: 2, width: 64, background: "#8A9E85" }} />
);

// — Sections —

const Hero = () => (
    <section
        data-testid="intensive-hero"
        className="relative pt-40 pb-28 lg:pt-48 lg:pb-36 overflow-hidden"
        style={{ background: "#F0DED6" /* warm dusty rose wash */ }}
    >
        {/* corner ornaments — preserved */}
        <div className="absolute top-32 left-12 hidden lg:block" aria-hidden>
            <div style={{ height: 1, width: 80, background: "#9B7D5A" }} />
            <div className="mt-3 text-gold text-sm">✦</div>
        </div>
        <div className="absolute bottom-24 right-12 hidden lg:block" aria-hidden>
            <div className="text-gold text-sm mb-3 text-right">✦</div>
            <div style={{ height: 1, width: 80, background: "#9B7D5A" }} />
        </div>

        <div className="relative mx-auto max-w-[900px] px-6 lg:px-12 text-center" data-reveal>
            <Eyebrow>A Different Way to Work</Eyebrow>
            <h1 className="mt-6 font-serif text-5xl sm:text-6xl lg:text-[76px] leading-[1.04] text-charcoal text-balance">
                EMDR Intensives for <em className="italic text-gold">Deep,<br className="hidden sm:block" /> Focused Healing</em>
            </h1>
            <div className="mt-8"><SageRule /></div>
            <p className="mt-10 max-w-2xl mx-auto text-lg sm:text-xl leading-[1.85] text-charcoal-soft text-pretty">
                You are ready to do the work. You just cannot give up your Tuesdays for the next
                six months. One focused day — not another standing weekly commitment — is how
                you do it.
            </p>
            <div className="mt-12">
                <CTA testid="intensive-hero-cta" />
            </div>
        </div>
    </section>
);

const Problem = () => (
    <section data-testid="intensive-problem" className="py-28 lg:py-36" style={{ background: "#FAFAF8" }}>
        {/* Widened content column — was max-w-[800px] */}
        <div className="mx-auto max-w-[1120px] px-6 lg:px-16 text-center" data-reveal>
            <Eyebrow>A Different Way to Invest</Eyebrow>
            <h2 className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.08] text-charcoal text-balance">
                A Different Way to Invest in <em className="italic text-gold">the Work</em>
            </h2>
            <div className="mt-8"><SageRule /></div>

            <div className="mt-14 text-left mx-auto max-w-[960px]">
                <p className="text-2xl sm:text-3xl font-serif italic text-charcoal drop-cap">
                    You already know there is still work to do.
                </p>
                <p className="mt-10 text-lg leading-[1.95] text-charcoal-soft">
                    Maybe you have been in therapy before — weekly sessions, real commitment, genuine
                    effort. But your life has shifted. Work became more demanding. Kids arrived, or
                    schedules filled in ways that feel permanent rather than temporary. The version
                    of you who could hold a standing weekly appointment for the foreseeable future
                    does not quite fit the life you are living now.
                </p>
                <p className="mt-10 text-lg leading-[1.95] text-charcoal-soft">
                    That does not mean the need for the work has gone anywhere. It means you need a
                    different format for it.
                </p>
                <p className="mt-10 text-lg leading-[1.95] text-charcoal-soft">
                    You want one focused day — maybe once a month, maybe once a quarter — where you
                    can go deep, move something real, and leave having actually changed something.
                    Not just processed something. Changed it.
                </p>
                <p className="mt-10 text-lg leading-[1.95] text-charcoal-soft">
                    And maybe there is something else underneath the scheduling piece. Maybe the idea
                    of committing to weekly therapy right now feels like adding one more thing to an
                    already full life. Maybe part of you worries you will start and then have to stop,
                    and starting and stopping feels worse than simply waiting for the right time. An
                    intensive removes that equation entirely. You commit to one experience. You show
                    up fully. And then you return to your life.
                </p>
                <p className="mt-12 text-2xl sm:text-3xl leading-[1.4] font-serif italic text-charcoal">
                    That is what an intensive is.
                </p>

                {/* Existing paragraphs preserved */}
                <p className="mt-10 text-lg leading-[1.95] text-charcoal-soft">
                    Intensives are not a measure of how stuck you are or a verdict on weekly therapy.
                    They are a format — a way of pacing the work that fits how you actually want to
                    invest your time, attention, and energy in this process.
                </p>
                <p className="mt-10 text-lg leading-[1.95] text-charcoal-soft">
                    For some clients, that means a full day devoted to one specific piece. For others,
                    it looks like fewer, longer sessions on a monthly or quarterly rhythm rather than
                    the pull of a standing weekly commitment. Either way, the emphasis is on depth,
                    sustained focus, and momentum.
                </p>
            </div>
        </div>
    </section>
);

const intensiveReasons = [
    "prefer one significant, focused investment over many smaller weekly sessions",
    "want to go deep in an immersive experience rather than build gradually over time",
    "are ready to dedicate a full day or extended session to a specific piece of the work",
    "do better with less frequent but more concentrated support, monthly or quarterly rather than weekly",
    "want to move through something specific with sustained focus and momentum",
];

const HowItHelps = () => (
    <section data-testid="intensive-howithelps" className="py-28 lg:py-36" style={{ background: "#EEF2ED" }}>
        <div className="mx-auto max-w-[1300px] px-6 lg:px-12">
            <div className="grid lg:grid-cols-12 gap-12 lg:gap-20">
                <div className="lg:col-span-7" data-reveal>
                    <Eyebrow>The Intensive Format</Eyebrow>
                    <h2 className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-charcoal">
                        How EMDR Intensives <em className="italic text-gold">Help</em>
                    </h2>
                    <div className="my-6" style={{ height: 2, width: 64, background: "#8A9E85" }} />
                    <p className="mt-6 text-lg leading-[1.95] text-charcoal-soft">
                        EMDR Intensives allow us to work in a more focused, immersive, and intentional way.
                    </p>
                    <p className="mt-6 text-lg leading-[1.95] text-charcoal-soft">
                        Rather than stopping and restarting every week, intensives create space to process
                        more deeply while maintaining structure, pacing, and emotional support throughout
                        the experience.
                    </p>

                    <div className="mt-12">
                        <p className="font-serif italic text-xl text-charcoal mb-6">
                            Many clients choose an intensive because they…
                        </p>
                        <ul className="space-y-4" data-testid="intensive-reasons">
                            {intensiveReasons.map((r) => (
                                <li key={r} className="flex items-start gap-3 text-charcoal-soft text-lg leading-[1.85]">
                                    <span className="text-gold leading-none mt-2 flex-shrink-0">✦</span>
                                    <span>{r}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>

                <aside className="lg:col-span-5 lg:pt-16" data-reveal>
                    <blockquote
                        style={{ borderLeft: "3px solid #9B7D5A", paddingLeft: "24px" }}
                        className="font-serif italic text-charcoal"
                    >
                        <p className="leading-[1.45]" style={{ fontSize: "24px" }}>
                            "What I love about intensives is the momentum they create. Clients often
                            experience breakthroughs that feel difficult to access in shorter weekly
                            sessions because there is enough space to fully stay with the work rather
                            than continually pausing it. This work is not rushed. It is thoughtful,
                            grounded, and deeply personalized."
                        </p>
                        <footer className="mt-6 text-[11px] tracking-[0.32em] uppercase not-italic" style={{ color: "#9B7D5A", fontFamily: "Figtree" }}>
                            <span className="mr-3">✦</span> Austine, LPC
                        </footer>
                    </blockquote>
                </aside>
            </div>
        </div>
    </section>
);

const ThePractice = () => (
    <section data-testid="intensive-practice" className="relative py-28 lg:py-36 overflow-hidden" style={{ background: "#F2EDE6" }}>
        <div className="relative mx-auto max-w-[1200px] px-6 lg:px-16 text-center" data-reveal>
            <Eyebrow>The Experience</Eyebrow>
            <h2 className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-charcoal text-balance">
                A Deeply Personalized Intensive <em className="italic text-gold">Experience</em>
            </h2>
            <div className="mt-8"><SageRule /></div>

            <div className="mt-12 text-left mx-auto max-w-[960px]">
                <p className="text-lg leading-[1.95] text-charcoal-soft">
                    EMDR Intensives at Alchemy Practice are intentionally structured around depth,
                    emotional safety, and individualized care. Alchemy Practice offers EMDR intensives
                    to clients throughout Wisconsin and Colorado via secure telehealth.
                </p>
                <p className="mt-6 text-lg leading-[1.95] text-charcoal-soft">
                    This is not a one-size-fits-all therapy experience. Every intensive is tailored to
                    your emotional history, goals, nervous system capacity, and the pace your system
                    actually needs. If a weekly rhythm feels like a better fit for where you are, you can
                    also read about <a href="/emdr" className="underline decoration-gold underline-offset-4 hover:text-gold" data-testid="intensive-link-emdr">standard EMDR therapy</a> and
                    <a href="/approach" className="underline decoration-gold underline-offset-4 hover:text-gold ml-1" data-testid="intensive-link-approach">the wider approach and modalities</a> offered here.
                </p>
            </div>

            {/* Three-stage structure — Part I / Part II / Part III */}
            <div className="relative mt-16 grid md:grid-cols-3 gap-6 lg:gap-8" data-testid="intensive-two-stage">
                {[
                    {
                        n: "I",
                        title: "Intake and Preparation",
                        body: (
                            <p className="text-charcoal-soft leading-[1.9] text-base">
                                Before your intensive, we meet for a dedicated preparation session. We take a
                                careful history, explore what you want to work through, and build the grounding
                                and resourcing skills that will support you throughout the intensive itself.
                                You may also receive brief between-session practices to help you arrive feeling
                                prepared and resourced.
                            </p>
                        ),
                    },
                    {
                        n: "II",
                        title: "The Intensive Session",
                        body: (
                            <div className="space-y-5">
                                <p className="text-charcoal-soft leading-[1.9] text-base">
                                    Your intensive session typically runs between three and five hours, with a
                                    structured break built in for any session over four hours. For clients who
                                    are new to EMDR or to intensive work, this length allows enough time to move
                                    through material thoroughly and at a pace that feels grounded, not rushed.
                                    For clients who already have experience with EMDR in a weekly format, a
                                    shorter session of two hours may be appropriate — we will discuss what makes
                                    sense for your goals during your consultation.
                                </p>
                                <p className="text-charcoal-soft leading-[1.9] text-base">
                                    Throughout the intensive, we move through the full EMDR process. Without the
                                    constraint of a 53-minute hour, the work has room to develop, resolve, and
                                    integrate. You will not leave mid-process.
                                </p>
                            </div>
                        ),
                    },
                    {
                        n: "III",
                        title: "Follow-Up and Integration",
                        body: (
                            <p className="text-charcoal-soft leading-[1.9] text-base">
                                The week after your intensive, we connect for a complimentary 30-minute follow-up
                                call. This is not another session — it is a space to check in on how things are
                                settling, what you are noticing, and whether anything has surfaced that needs
                                attention. We also use this time to talk about what comes next: another intensive
                                in a month or two, a return to weekly sessions, or simply the continued work of
                                living with what shifted.
                            </p>
                        ),
                    },
                ].map((stage) => (
                    <article
                        key={stage.n}
                        data-testid={`intensive-stage-${stage.n.toLowerCase()}`}
                        className="text-left p-10 lg:p-12"
                        style={{ background: "#FAFAF8", boxShadow: "0 1px 0 rgba(44,40,37,0.04)", borderTop: "1px solid #E2DDD8" }}
                    >
                        <div className="flex items-baseline justify-between mb-6">
                            <span className="font-serif text-3xl text-gold">Part {stage.n}</span>
                            <span className="font-serif text-gold text-lg leading-none">✦</span>
                        </div>
                        <h3 className="font-serif text-2xl sm:text-3xl text-charcoal leading-snug">{stage.title}</h3>
                        <div className="h-px w-10 my-6" style={{ background: "#8A9E85" }} />
                        {stage.body}
                    </article>
                ))}
            </div>
        </div>
    </section>
);

const faqs = [
    {
        q: "Can an intensive replace ongoing therapy?",
        a: "For some people and some goals, yes — particularly when the focus is specific and bounded. For others, an intensive is a powerful complement to ongoing work, creating a significant shift that weekly sessions can then build on.",
    },
    {
        q: "Do I need to have done EMDR before to do an intensive?",
        a: "Not necessarily, but some prior therapy experience is generally helpful. During our consultation we will assess readiness and make sure you have the stabilization resources needed to engage with intensive work safely.",
    },
    {
        q: "Are EMDR Intensives available via telehealth?",
        a: "Yes. Intensives are available virtually throughout Wisconsin and Colorado, following the same secure telehealth format as standard sessions.",
    },
    {
        q: "What does an EMDR Intensive cost?",
        a: "EMDR Intensives are billed at $150 per hour. Sessions have a minimum length of two hours and a maximum of six hours. For any intensive scheduled over four hours, a one-hour break is built into the session. Intensives are not covered by insurance, but FSA and HSA cards may be used for payment. A superbill can also be provided for potential out-of-network reimbursement. We will discuss the right length for your goals during your free consultation.",
    },
];

const FAQItem = ({ q, a, idx }) => {
    const [open, setOpen] = useState(idx === 0);
    return (
        <div className="border-b" style={{ borderColor: "#E2DDD8" }} data-testid={`intensive-faq-${idx}`}>
            <button
                onClick={() => setOpen(!open)}
                className="w-full flex items-start justify-between gap-6 py-7 text-left group"
                data-testid={`intensive-faq-trigger-${idx}`}
            >
                <span className="font-serif text-2xl sm:text-3xl text-charcoal pr-4 group-hover:text-gold transition-colors duration-500">
                    {q}
                </span>
                <span className="flex-shrink-0 mt-2 text-gold">
                    {open ? <Minus strokeWidth={1.25} size={22} /> : <Plus strokeWidth={1.25} size={22} />}
                </span>
            </button>
            <div className={`grid transition-all duration-700 ease-out ${open ? "grid-rows-[1fr] opacity-100 pb-8" : "grid-rows-[0fr] opacity-0"}`}>
                <div className="overflow-hidden">
                    <p className="max-w-3xl text-charcoal-soft text-lg leading-[1.85] pr-12">{a}</p>
                </div>
            </div>
        </div>
    );
};

const FAQ = () => (
    <section data-testid="intensive-faq-section" className="py-28 lg:py-36" style={{ background: "#FAFAF8" }}>
        <div className="mx-auto max-w-[1000px] px-6 lg:px-12">
            <div className="text-center mb-12" data-reveal>
                <Eyebrow>Considerations</Eyebrow>
                <h2 className="mt-5 font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-charcoal">
                    Questions About <em className="italic text-gold">Intensives</em>
                </h2>
                <div className="mt-8"><SageRule /></div>
            </div>
            <div data-reveal>
                {faqs.map((f, i) => <FAQItem key={i} idx={i} q={f.q} a={f.a} />)}
            </div>
        </div>
    </section>
);

const FinalCTA = () => (
    <section data-testid="intensive-final-cta" className="py-28 lg:py-36" style={{ background: "#EEF2ED" }}>
        <div className="mx-auto max-w-[900px] px-6 lg:px-12 text-center" data-reveal>
            <Eyebrow>Begin</Eyebrow>
            <h2 className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-charcoal text-balance">
                Ready to Explore Whether an <em className="italic text-gold">Intensive</em> Is Right for You?
            </h2>
            <p className="mt-10 max-w-2xl mx-auto text-lg sm:text-xl leading-[1.85] text-charcoal-soft text-pretty">
                Schedule a free consultation and we will talk through your goals, your history, and
                whether an EMDR Intensive makes sense for where you are.
            </p>
            <div className="mt-12">
                <CTA testid="intensive-final-cta-btn" />
            </div>
            <div className="mt-10 flex flex-wrap items-center justify-center gap-x-8 gap-y-2 text-[11px] tracking-[0.28em] uppercase" style={{ color: "#8A7968" }}>
                <span className="flex items-center gap-3"><span className="text-gold text-base leading-none">✦</span> Secure Telehealth</span>
                <span className="flex items-center gap-3"><span className="text-gold text-base leading-none">✦</span> Wisconsin & Colorado</span>
                <span className="flex items-center gap-3"><span className="text-gold text-base leading-none">✦</span> By Appointment</span>
            </div>
        </div>
    </section>
);

const intensivePhrases = [
    "Sometimes healing needs more space than an hour a week can hold.",
    "An intensive is not a shortcut. It is an invitation to go deeper.",
    "When you are ready, the work can move at the pace of your readiness.",
    "Concentrated healing is still healing — sometimes more so.",
];

// Service-level schema — describes EMDR Intensives as a specific medical
// therapy offered by Alchemy Practice across Wisconsin and Colorado.
const intensiveJsonLd = {
    "@context": "https://schema.org",
    "@type": "MedicalTherapy",
    name: "EMDR Intensive Therapy",
    alternateName: "EMDR Intensive",
    description:
        "Extended, concentrated EMDR therapy sessions for clients who prefer one focused day of depth work over an ongoing weekly commitment. Delivered virtually throughout Wisconsin and Colorado by Alchemy Practice.",
    url: "https://alchemypractice.com/emdr-intensive",
    provider: {
        "@type": "MedicalBusiness",
        "@id": "https://alchemypractice.com/#business",
        name: "Alchemy Practice",
    },
    areaServed: [
        { "@type": "State", name: "Wisconsin" },
        { "@type": "State", name: "Colorado" },
    ],
    availableService: {
        "@type": "MedicalProcedure",
        procedureType: "Psychotherapy",
    },
    offers: {
        "@type": "Offer",
        priceCurrency: "USD",
        price: "150",
        unitText: "HUR",
        description:
            "EMDR Intensives are billed at $150 per hour. Sessions run two to six hours. Sessions over four hours include a built-in one-hour break.",
    },
};

export default function EMDRIntensivePage() {
    return (
        <main className="bg-ivory text-charcoal">
            <Seo
                title="EMDR Intensives in Wisconsin & Colorado | Alchemy Practice"
                description="Concentrated EMDR therapy for people ready to go deeper. Extended virtual sessions serving Wisconsin and Colorado. Alchemy Practice with Austine LPC."
                jsonLd={intensiveJsonLd}
            />
            <Nav />
            <RevealOnScroll>
                <Hero />
                <SectionDivider color="gold" bg="#FAFAF8" />
                <Problem />
                <SectionDivider color="sage" bg="#FAFAF8" />
                <HowItHelps />
                <SectionDivider color="rose" bg="#EEF2ED" />
                <ThePractice />
                <SectionDivider color="gold" bg="#F2EDE6" />
                <FAQ />
                <SectionDivider color="sage" bg="#FAFAF8" />
                <FinalCTA />
                <Marquee phrases={intensivePhrases} background="#4A7B79" />
                <Footer />
            </RevealOnScroll>
        </main>
    );
}
